(ns manatee.components.editors.toolbar
  (:require
   [manatee.annotation.handlers :as handlers]
   [manatee.domain.task :as task-config]
   [re-frame.core :as rf]
   [rothko.editor.toolbar.component :as t]))

(def undo {::t/component t/action-button
           ::t/name "Undo"
           ::t/action :undo
           ::t/icon :undo})

(def redo {::t/component t/action-button
           ::t/name "Redo"
           ::t/action :redo
           ::t/icon :redo})

(def separator {::t/component t/separator})

(def hesitation {::t/component t/action-button
                 ::t/name "Hesitation"
                 ::t/action :hesitation
                 ::t/representation "⋯"})

(def unknown {::t/component t/action-button
              ::t/name "Unknown"
              ::t/action :unknown
              ::t/representation "‽"})

(def code-switch {::t/component t/action-button
                  ::t/name "Code switch"
                  ::t/action :code-switch
                  ::t/representation "ƒ"})

(def foreign-proper-noun {::t/component t/action-button
                          ::t/name "Foreign proper noun"
                          ::t/action :foreign-proper-noun
                          ::t/representation "Ƥ"})

(def bc-backchannel {::t/component t/action-button
                     ::t/name "BC (backchannel)"
                     ::t/action :bc-backchannel
                     ::t/representation "BC"})

(def speech-unit-statment {::t/component t/action-button
                           ::t/name "Speech unit - statement"
                           ::t/action :speech-unit-statement
                           ::t/representation "/."})

(def speech-unit-question {::t/component t/action-button
                           ::t/name "Speech unit - question"
                           ::t/action :speech-unit-question
                           ::t/representation "/?"})

(def speech-unit-incomplete {::t/component t/action-button
                             ::t/name "Speech unit - incomplete"
                             ::t/action :speech-unit-incomplete
                             ::t/representation "/-"})

(def mwe {::t/component t/action-button
          ::t/name "Multi-Word Expression"
          ::t/action :mwe
          ::t/representation "⟪…⟫"})

(def original {::t/component t/view-button
               ::t/name "Original"
               ::t/view-state :original})

(def annotated {::t/component t/view-button
                ::t/name "Annotated"
                ::t/view-state :annotated})

(def corrected {::t/component t/view-button
                ::t/name "Corrected"
                ::t/view-state :corrected})

(def original-before {::t/component t/view-button
                      ::t/name "Before"
                      ::t/view-state :original})

(def annotated-now {::t/component t/view-button
                    ::t/name "Now"
                    ::t/view-state :annotated})

(def corrected-after {::t/component t/view-button
                      ::t/name "After"
                      ::t/view-state :corrected})

(def toggle-ro-view {::t/component t/toggle-ro-view-button
                     ::t/name "Split"})

(def ro-view-original {::t/component t/ro-view-button
                       ::t/name "Before"
                       ::t/view-state :ro-original})

(def ro-view-corrected {::t/component t/ro-view-button
                        ::t/name "After"
                        ::t/view-state :ro-corrected})

(def delete-all-marks {::t/component t/action-button
                       :appearance #{:warning}
                       ::t/name "Delete marks and tags"
                       ::t/action :delete-marks
                       ::t/icon :bin})

(def remove-changes {::t/component t/action-button
                     :appearance #{:warning}
                     ::t/name "Remove changes"
                     ::t/action :remove-changes
                     ::t/icon :bin})

(defn split-segment
  [editor-event-opts]
  {::t/component t/action-button
   :appearance #{:partial}
   ::t/name "Split segment"
   ::t/action :split-segment
   ::t/icon :split
   ::t/effect-fn #(rf/dispatch [::handlers/split-segment editor-event-opts %])})

(defn markers->toolbar-items
  [markers]
  (if (empty? markers)
    []
    (mapv (fn [{:keys [value label]}]
            {::t/component t/action-button
             :appearance #{(keyword value)}
             ::t/name label
             ::t/action (keyword value)})
          markers)))

(defn make-toolbar [items default-markers]
  (fn [_ markers]
    (let [marker-tools (markers->toolbar-items (or markers default-markers))]
      (if (empty? marker-tools)
        items
        (concat items [separator] marker-tools)))))

(defn make-transcript-annotation-toolbar
  [{:keys [segment-annotation?] :as editor-event-opts} markers]
  (let [items (->> [undo
                    redo

                    separator

                    hesitation unknown code-switch foreign-proper-noun
                    (when segment-annotation? bc-backchannel)

                    separator

                    speech-unit-statment speech-unit-question speech-unit-incomplete

                    separator

                    original-before annotated-now corrected-after toggle-ro-view

                    separator

                    (when segment-annotation? (split-segment editor-event-opts))
                    delete-all-marks]
                   (remove nil?))
        ;; The :value here must match the values in the marker config in src/client/manatee/components/item.cljs
        ;; Ditto for the other toolbar constructors further down.
        default-markers [{:value "disfluency" :label "Disfluency"}
                         {:value "pronunciation", :label "Pronunciation", :representation (when segment-annotation? "Pron")}
                         {:value "partial", :label "Partial"}
                         {:value "backchannel", :label "Backchannel"}]]
    ((make-toolbar items default-markers) editor-event-opts markers)))

(def error-annotation-toolbar
  (make-toolbar [undo
                 redo

                 separator

                 unknown
                 foreign-proper-noun

                 separator

                 speech-unit-statment
                 speech-unit-question
                 speech-unit-incomplete

                 separator

                 original-before
                 annotated-now
                 corrected-after
                 toggle-ro-view

                 separator

                 delete-all-marks]
                [{:value "spelling" :label "Phase 2 slip-up"}]))

(defn make-script-annotation-toolbar
  [{:keys [task-config] :as editor-event-opts} markers]
  (let [items (remove nil? [undo
                            redo

                            separator

                            unknown
                            (when (task-config/modifier task-config :mwe-marker?)
                              mwe)

                            separator

                            original
                            annotated
                            corrected
                            toggle-ro-view

                            separator

                            remove-changes])
        default-markers []]
    ((make-toolbar items default-markers) editor-event-opts markers)))

(def annotation-ro-view-toolbar
  [ro-view-original
   ro-view-corrected])

(def mwe-annotation-toolbar
  (make-toolbar [undo
                 redo

                 separator

                 mwe

                 separator

                 original
                 annotated

                 separator

                 delete-all-marks]
                [{:value "discontinuity" :label "Discontinuity"}]))

(defn toolbar
  [props toolbar-items]
  [t/toolbar (assoc props ::t/config toolbar-items)])
